// @ts-check

/**
 * @file Renders a paginated async organization select.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import ModelSelect from "../model-select"
import {Organization} from "../../frontend-models/organization"
import {MODEL_SELECT_PAGE_SIZE, modelSelectOptionsResult, normalizedSelectValues} from "../model-select-options"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} OrganizationsSelectProps
 * @property {string} [blankOptionText] Optional empty option text.
 * @property {boolean} [creditor] Limit options to creditor organizations.
 * @property {boolean} [customer] Limit options to customer organizations.
 * @property {string | number | null} [defaultValue] Initial selected organization id.
 * @property {(string | number)[]} [defaultValues] Initial selected organization ids.
 * @property {string} [label] Select label.
 * @property {boolean} [multiple] Allow multiple selections.
 * @property {string} [name] Api Maker form field name.
 * @property {(...args: unknown[]) => unknown} [onChangeValue] Selection change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id.
 * @property {(string | number)[]} [values] Controlled selected values.
 */

/** @typedef {Record<string, never>} OrganizationsSelectState */

/**
 * Organization select with optional role filters.
 * @augments {ShapeComponent<OrganizationsSelectProps, OrganizationsSelectState>}
 */
class OrganizationsSelect extends ShapeComponent {
  static propTypes = propTypesExact({
    blankOptionText: PropTypes.string,
    creditor: PropTypes.bool,
    customer: PropTypes.bool,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    ),
    label: PropTypes.string,
    multiple: PropTypes.bool,
    name: PropTypes.string,
    onChangeValue: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    )
  })
  static defaultProps = {
    blankOptionText: undefined,
    creditor: undefined,
    customer: undefined,
    defaultValue: undefined,
    defaultValues: undefined,
    label: undefined,
    multiple: false,
    name: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    values: undefined
  }

  /** Initializes locale support. */
  setup() {
    useLocale()
  }

  /**
   * Renders the select.
   * @returns {ReactNode} Organization select.
   */
  render() {
    const {
      defaultValue,
      defaultValues,
      label,
      multiple,
      name,
      onChangeValue,
      placeholder,
      testID,
      values
    } = this.p
    return (
      <ModelSelect
        defaultValue={defaultValue}
        defaultValues={defaultValues}
        label={label}
        multiple={multiple}
        name={name}
        onChangeValue={onChangeValue}
        options={this.tt.loadOptions}
        placeholder={placeholder || this.p.blankOptionText || _("Search organizations...")}
        testID={testID}
        values={values}
      />
    )
  }

  /**
   * Loads organization options for HayaSelect.
   * @param {{page?: number, searchValue?: string, values?: (string | number)[] | string | number}} args Loader args.
   * @returns {Promise<{options: {text: string, value: string}[], page: number, pageSize: number, totalCount: number}>} Paginated options.
   */
  loadOptions = async ({page = 1, searchValue, values} = {}) => {
    const selectedValues = normalizedSelectValues(values)
    if (selectedValues.length > 0) {
      const organizations = await Organization
        .where({id: selectedValues})
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .toArray()
      return modelSelectOptionsResult({
        options: organizations.map(organizationToOption),
        page: 1,
        totalCount: organizations.length
      })
    }

    /** @type {Record<string, unknown>} */
    const ransackParams = {}
    if (searchValue) ransackParams.nameCont = searchValue
    const baseQuery = this.baseQuery(ransackParams)
    const includeBlankOption = Boolean(this.p.blankOptionText) && !searchValue
    const pageSize = includeBlankOption && page === 1
      ? MODEL_SELECT_PAGE_SIZE - 1
      : MODEL_SELECT_PAGE_SIZE
    const offset = includeBlankOption
      ? Math.max((page - 1) * MODEL_SELECT_PAGE_SIZE - 1, 0)
      : (page - 1) * MODEL_SELECT_PAGE_SIZE
    const [organizations, organizationCount] = await Promise.all([
      baseQuery
        .clone()
        .select(["id", "name"])
        .order([["name", "asc"], ["id", "asc"]])
        .offset(offset)
        .limit(pageSize)
        .toArray(),
      baseQuery.clone().count()
    ])
    const options = organizations.map(organizationToOption)
    if (includeBlankOption && page === 1 && this.p.blankOptionText) {
      options.unshift({
        text: this.p.blankOptionText,
        value: ""
      })
    }
    return modelSelectOptionsResult({
      options,
      page,
      totalCount: organizationCount + (includeBlankOption ? 1 : 0)
    })
  }

  /**
   * Builds the base organization query for current filters.
   * @param {Record<string, unknown>} ransackParams Ransack search params.
   * @returns {ReturnType<typeof Organization.ransack>} Organization query.
   */
  baseQuery(ransackParams) {
    let query = Organization.ransack(ransackParams)
    if (this.p.creditor === true) {
      query = query.where({creditor: true})
    }
    if (this.p.customer === true) {
      query = query.where({customer: true})
    }
    return query
  }
}

/**
 * Converts an organization model into a select option.
 * @param {InstanceType<typeof Organization>} organization Organization model.
 * @returns {{text: string, value: string}} Select option.
 */
function organizationToOption(organization) {
  return {
    text: organization.name() || String(organization.id()),
    value: String(organization.id())
  }
}

/** @type {NamedExoticComponent<OrganizationsSelectProps>} */
const OrganizationsSelectComponent = memo(shapeComponent(OrganizationsSelect))

export default OrganizationsSelectComponent
